import { Link } from "wouter";
import { Plane, FileWarning } from "lucide-react";
import { useLanguage } from "@/contexts/language";

const EFFECTIVE_DATE_EN = "April 2, 2026";
const EFFECTIVE_DATE_FR = "2 avril 2026";

export default function Waiver() {
  const { language, setLanguage } = useLanguage();
  const isFr = language === "fr";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card px-6 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="bg-primary/20 p-1.5 rounded-md">
            <Plane className="h-5 w-5 text-primary" />
          </div>
          <span className="font-bold tracking-tight text-primary font-mono">FLIGHT DECK</span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="flex gap-4 text-xs font-mono text-muted-foreground">
            <Link href="/privacy" className="hover:text-foreground transition-colors">
              {isFr ? "Confidentialité" : "Privacy"}
            </Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">
              {isFr ? "Conditions" : "Terms"}
            </Link>
            <Link href="/waiver" className="text-primary underline underline-offset-2">
              {isFr ? "Avertissement" : "Waiver"}
            </Link>
          </div>
          <button
            onClick={() => setLanguage(isFr ? "en" : "fr")}
            className="text-xs font-mono font-semibold text-primary border border-primary/40 rounded px-2 py-0.5 hover:bg-primary/10 transition-colors"
          >
            {isFr ? "EN" : "FR"}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        {isFr ? <WaiverFr /> : <WaiverEn />}

        <div className="border-t border-border pt-8 flex flex-wrap gap-4 text-xs font-mono text-muted-foreground">
          <Link href="/privacy" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Politique de confidentialité" : "Privacy Policy"}
          </Link>
          <Link href="/terms" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Conditions d'utilisation" : "Terms of Service"}
          </Link>
          <Link href="/contact" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Assistance" : "Support"}
          </Link>
          <Link href="/" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Retour à l'application" : "Back to App"}
          </Link>
          <span className="ml-auto">© {new Date().getFullYear()} Flight Deck CPL Exam Prep</span>
        </div>
      </div>
    </div>
  );
}

function WaiverEn() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileWarning className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Disclaimer & Liability Waiver</h1>
        </div>
        <p className="text-xs text-muted-foreground font-mono">
          Effective Date: {EFFECTIVE_DATE_EN} &nbsp;·&nbsp; This document forms part of our{" "}
          <Link href="/terms" className="text-primary underline underline-offset-2">Terms of Service</Link>
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        By accessing or using this service you acknowledge that you have read and understood this disclaimer. Flight Deck CPL Exam Prep is an independent study aid and is not affiliated with, endorsed by, or approved by Transport Canada, the Government of Canada, or any aviation regulatory authority.
      </p>

      <Section title="1. Nature of the Service — Study Aid Only">
        <P>
          Flight Deck CPL Exam Prep is a self-study tool intended solely to assist candidates in preparing for Transport Canada written examinations. It is <strong>not</strong>:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>An official Transport Canada examination or preparation material.</li>
          <li>A replacement for certified ground school instruction, official study guides, or any other Transport Canada approved material.</li>
          <li>A substitute for practical flight training, a flight instructor, or any qualified aviation professional.</li>
          <li>A guarantee or indicator of readiness to conduct safe flight operations.</li>
        </ul>
        <P>
          You remain solely responsible for ensuring that you meet all eligibility requirements, have completed all required training, and are adequately prepared before attempting any official Transport Canada examination or conducting any flight operation.
        </P>
      </Section>

      <Section title="2. No Warranty on Content Accuracy">
        <P>
          The questions, answers, explanations, and other content provided on this platform are created independently and are <strong>not sourced from or verified by Transport Canada</strong>. We make no representation or warranty — express or implied — that the content is:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Accurate, correct, complete, or current.</li>
          <li>Consistent with the content, format, difficulty, or weighting of any official Transport Canada examination.</li>
          <li>Reflective of current Canadian Aviation Regulations (CARs), Transport Canada circulars, or any other official aviation documentation.</li>
          <li>Free from errors, omissions, or outdated information.</li>
        </ul>
        <P>
          Aviation regulations, procedures, and standards change regularly. Content on this platform may not reflect the most recent regulatory updates. You are responsible for consulting current official sources — including Transport Canada publications, the <em>Aeronautical Information Manual (AIM)</em>, and current CARs — for authoritative and up-to-date information.
        </P>
      </Section>

      <Section title="3. Assumption of Risk">
        <P>
          You expressly acknowledge and assume all risks associated with using this platform, including but not limited to:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>The risk that practice questions may contain errors, inaccuracies, or misleading information.</li>
          <li>The risk that reliance on this platform may not adequately prepare you for the official Transport Canada examination.</li>
          <li>The risk of failing an official examination after using this service.</li>
          <li>The risk that content may not reflect current regulations, procedures, or standards.</li>
          <li>The risk that platform availability may be interrupted due to technical issues, maintenance, or other factors.</li>
          <li>Any other risk arising from the use of an independent, non-government-affiliated study tool in preparation for a safety-critical aviation examination.</li>
        </ul>
        <P>
          You acknowledge that aviation is a safety-critical field and that inadequate preparation — regardless of the tools used — may have serious consequences. You agree that any decision to attempt an official examination or conduct flight operations based on your preparation is your own informed decision, made voluntarily and at your sole risk.
        </P>
      </Section>

      <Section title="4. Release of Liability — Waiver of Claims">
        <P>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE CANADIAN LAW, BY USING THIS SERVICE YOU KNOWINGLY AND VOLUNTARILY:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>RELEASE, WAIVE, AND DISCHARGE</strong> Flight Deck CPL Exam Prep, its operators, owners, employees, contractors, and affiliates (collectively, "Released Parties") from any and all claims, demands, actions, liabilities, losses, costs, or damages of any nature — whether known or unknown — arising out of or relating to your use of the service.</li>
          <li><strong>AGREE NOT TO SUE</strong> the Released Parties for any loss or damage arising from the inaccuracy of content, failure to pass an official examination, or any other outcome connected to your use of the platform.</li>
          <li><strong>INDEMNIFY AND HOLD HARMLESS</strong> the Released Parties from any claim brought by a third party arising from your use of the service or your reliance on the content provided.</li>
        </ul>
        <P>
          This release includes, without limitation, claims for: examination failure; inaccurate or outdated question content; loss of examination fees; loss of employment or career opportunity; and any direct, indirect, incidental, special, consequential, or punitive damages of any kind.
        </P>
        <P>
          Nothing in this Waiver limits liability for gross negligence, willful misconduct, fraud, or any other matter that cannot be lawfully excluded or limited under Canadian law.
        </P>
      </Section>

      <Section title="5. Aviation Safety Acknowledgement">
        <P>
          You acknowledge that:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>A Commercial Pilot Licence (CPL) issued by Transport Canada is a safety-critical credential directly related to the operation of aircraft and the safety of passengers, crew, and the public.</li>
          <li>Adequate preparation for Transport Canada examinations requires the use of Transport Canada approved materials, certified ground school instruction, and guidance from qualified aviation professionals — in addition to any supplementary tools such as this platform.</li>
          <li>You will not rely solely on this platform as your primary or exclusive means of examination preparation.</li>
          <li>You will seek qualified professional instruction and consult current official Transport Canada publications as part of your preparation.</li>
        </ul>
      </Section>

      <Section title="6. Severability">
        <P>
          If any provision of this Waiver is found to be unenforceable or invalid under applicable Canadian law, the remaining provisions shall continue in full force and effect. The unenforceable provision shall be modified to the minimum extent necessary to make it enforceable.
        </P>
      </Section>

      <Section title="7. Governing Law">
        <P>
          This Disclaimer and Liability Waiver is governed by the laws of the Province of Ontario and the applicable federal laws of Canada. Any dispute arising from this Waiver shall be subject to the exclusive jurisdiction of the courts of the Province of Ontario.
        </P>
      </Section>

      <Section title="8. Acceptance">
        <P>
          Your use of this platform constitutes your unconditional acceptance of this Disclaimer and Liability Waiver in its entirety. If you do not accept these terms, you must immediately cease using the service.
        </P>
        <P>
          For any questions regarding this Waiver, please contact us through our <Link href="/contact" className="text-primary underline underline-offset-2">support form</Link>.
        </P>
      </Section>
    </>
  );
}

function WaiverFr() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileWarning className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Avertissement et renonciation à la responsabilité</h1>
        </div>
        <p className="text-xs text-muted-foreground font-mono">
          Date d'entrée en vigueur : {EFFECTIVE_DATE_FR} &nbsp;·&nbsp; Ce document fait partie de nos{" "}
          <Link href="/terms" className="text-primary underline underline-offset-2">Conditions d'utilisation</Link>
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        En accédant à ce service ou en l'utilisant, vous reconnaissez avoir lu et compris le présent avertissement. Flight Deck CPL Exam Prep est un outil d'autoapprentissage indépendant qui n'est pas affilié à Transports Canada, au gouvernement du Canada ou à toute autorité de réglementation de l'aviation, ni approuvé par ceux-ci.
      </p>

      <Section title="1. Nature du service — Outil d'étude uniquement">
        <P>
          Flight Deck CPL Exam Prep est un outil d'autoapprentissage destiné uniquement à aider les candidats à se préparer aux examens écrits de Transports Canada. Il n'est <strong>pas</strong> :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Un examen officiel de Transports Canada ni un matériel de préparation officiel.</li>
          <li>Un substitut aux cours au sol certifiés, aux guides d'étude officiels ou à tout autre matériel approuvé par Transports Canada.</li>
          <li>Un substitut à la formation pratique en vol, à un instructeur de vol ou à tout professionnel qualifié de l'aviation.</li>
          <li>Une garantie ou un indicateur de préparation à la conduite d'opérations de vol sécuritaires.</li>
        </ul>
        <P>
          Vous demeurez seul responsable de vous assurer que vous satisfaites à toutes les conditions d'admissibilité, avez complété toute la formation requise et êtes adéquatement préparé avant de tenter un examen officiel de Transports Canada ou de mener toute opération de vol.
        </P>
      </Section>

      <Section title="2. Aucune garantie quant à l'exactitude du contenu">
        <P>
          Les questions, réponses, explications et autres contenus fournis sur cette plateforme sont créés de manière indépendante et <strong>ne proviennent pas de Transports Canada et ne sont pas vérifiés par cet organisme</strong>. Nous ne faisons aucune déclaration ni garantie — expresse ou implicite — que le contenu est :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Exact, correct, complet ou à jour.</li>
          <li>Conforme au contenu, au format, à la difficulté ou à la pondération de tout examen officiel de Transports Canada.</li>
          <li>Reflétant le Règlement de l'aviation canadien (RAC) en vigueur, les circulaires de Transports Canada ou tout autre document officiel d'aviation.</li>
          <li>Exempt d'erreurs, d'omissions ou d'informations périmées.</li>
        </ul>
        <P>
          La réglementation, les procédures et les normes aéronautiques évoluent régulièrement. Le contenu de cette plateforme peut ne pas refléter les mises à jour réglementaires les plus récentes. Vous êtes responsable de consulter les sources officielles actuelles — y compris les publications de Transports Canada, le <em>Manuel d'information aéronautique (AIM)</em> et le RAC en vigueur — pour des renseignements fiables et à jour.
        </P>
      </Section>

      <Section title="3. Prise en charge des risques">
        <P>
          Vous reconnaissez expressément et assumez tous les risques associés à l'utilisation de cette plateforme, y compris, sans s'y limiter :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Le risque que les questions de pratique contiennent des erreurs, des inexactitudes ou des informations trompeuses.</li>
          <li>Le risque que l'utilisation de cette plateforme ne vous prépare pas adéquatement à l'examen officiel de Transports Canada.</li>
          <li>Le risque d'échouer à un examen officiel après avoir utilisé ce service.</li>
          <li>Le risque que le contenu ne reflète pas la réglementation, les procédures ou les normes en vigueur.</li>
          <li>Le risque que la disponibilité de la plateforme soit interrompue en raison de problèmes techniques, de maintenance ou d'autres facteurs.</li>
          <li>Tout autre risque découlant de l'utilisation d'un outil d'étude indépendant, non affilié au gouvernement, pour la préparation à un examen d'aviation critique pour la sécurité.</li>
        </ul>
        <P>
          Vous reconnaissez que l'aviation est un domaine critique pour la sécurité et qu'une préparation insuffisante — quels que soient les outils utilisés — peut avoir de graves conséquences. Vous acceptez que toute décision de tenter un examen officiel ou de mener des opérations de vol en fonction de votre préparation est votre propre décision éclairée, prise librement et à vos seuls risques.
        </P>
      </Section>

      <Section title="4. Dégagement de responsabilité — Renonciation aux réclamations">
        <P>
          DANS TOUTE LA MESURE PERMISE PAR LE DROIT CANADIEN APPLICABLE, EN UTILISANT CE SERVICE, VOUS RECONNAISSEZ ET ACCEPTEZ VOLONTAIREMENT :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>LIBÉRER, RENONCER ET DÉCHARGER</strong> Flight Deck CPL Exam Prep, ses exploitants, propriétaires, employés, sous-traitants et affiliés (collectivement, les « Parties libérées ») de toutes réclamations, demandes, actions, responsabilités, pertes, coûts ou dommages de quelque nature que ce soit — connus ou inconnus — découlant de ou liés à votre utilisation du service.</li>
          <li><strong>ACCEPTER DE NE PAS POURSUIVRE</strong> les Parties libérées pour toute perte ou dommage découlant de l'inexactitude du contenu, de l'échec à un examen officiel ou de tout autre résultat lié à votre utilisation de la plateforme.</li>
          <li><strong>INDEMNISER ET DÉGAGER DE TOUTE RESPONSABILITÉ</strong> les Parties libérées contre toute réclamation d'un tiers découlant de votre utilisation du service ou de votre recours au contenu fourni.</li>
        </ul>
        <P>
          Cette renonciation inclut, sans s'y limiter, les réclamations pour : échec à l'examen ; contenu de questions inexact ou périmé ; perte de frais d'examen ; perte d'emploi ou d'opportunité de carrière ; et tout dommage direct, indirect, accessoire, spécial, consécutif ou punitif de quelque nature que ce soit.
        </P>
        <P>
          Rien dans la présente Renonciation ne limite la responsabilité pour négligence grossière, faute intentionnelle, fraude ou toute autre question ne pouvant être légalement exclue ou limitée en vertu du droit canadien.
        </P>
      </Section>

      <Section title="5. Reconnaissance en matière de sécurité aéronautique">
        <P>
          Vous reconnaissez que :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Une licence de pilote commercial (LPC) délivrée par Transports Canada est une accréditation critique pour la sécurité, directement liée à l'exploitation d'aéronefs et à la sécurité des passagers, de l'équipage et du public.</li>
          <li>Une préparation adéquate aux examens de Transports Canada exige l'utilisation de matériel approuvé par Transports Canada, de cours au sol certifiés et des conseils de professionnels qualifiés de l'aviation — en plus de tout outil supplémentaire tel que cette plateforme.</li>
          <li>Vous ne vous fierez pas uniquement à cette plateforme comme principal ou unique moyen de préparation aux examens.</li>
          <li>Vous rechercherez une instruction professionnelle qualifiée et consulterez les publications officielles actuelles de Transports Canada dans le cadre de votre préparation.</li>
        </ul>
      </Section>

      <Section title="6. Divisibilité">
        <P>
          Si une disposition de la présente Renonciation est jugée inapplicable ou invalide en vertu du droit canadien applicable, les dispositions restantes continueront de s'appliquer pleinement. La disposition inapplicable sera modifiée dans la mesure minimale nécessaire pour la rendre applicable.
        </P>
      </Section>

      <Section title="7. Droit applicable">
        <P>
          Le présent Avertissement et la présente Renonciation à la responsabilité sont régis par les lois de la Province de l'Ontario et les lois fédérales applicables du Canada. Tout différend découlant de la présente Renonciation sera soumis à la juridiction exclusive des tribunaux de la Province de l'Ontario.
        </P>
      </Section>

      <Section title="8. Acceptation">
        <P>
          Votre utilisation de cette plateforme constitue votre acceptation inconditionnelle du présent Avertissement et de la présente Renonciation à la responsabilité dans leur intégralité. Si vous n'acceptez pas ces conditions, vous devez immédiatement cesser d'utiliser le service.
        </P>
        <P>
          Pour toute question concernant la présente Renonciation, veuillez nous contacter via notre <Link href="/contact" className="text-primary underline underline-offset-2">formulaire d'assistance</Link>.
        </P>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-bold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-foreground/80 leading-relaxed">{children}</p>;
}
